import React, { Component } from "react";
import { CreditRequestLineItem } from "../../common/CreditRequest";
import PageContext, { updatePageContext, getPageContext } from "../../components/Common/PageContext";
import { setStateAsync } from "../../util/stateHelper";
import { getCreditRequest, postCreditRequest, getReceiveById, postReceiveFinalise } from "../../services/apiHelper";
import CreditRequestHeader from "../../components/CreditRequest/CreditRequestHeader";
import CreditRequestList from "../../components/CreditRequest/CreditRequestList";
import { List, ListItem, Divider, Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import groupBy from "../../util/groupByUtil";
import { sortArrayByFirstValue, sortArrayByStatus } from "../../util/sortUtil";
import { creditReason, creditStatus } from "../../util/creditRequestMapping";
import { purchaseOrderStatus as purchaseOrderStatusMapping } from "../../util/purchaseOrderStatus";
import qs from "qs";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import CreditRequestDialog from "../../components/CreditRequest/CreditRequestDialog";
import SpeedDialMenu from "../../components/SpeedDial/SpeedDialMenu";
import { speedDialAction } from "../../util/speedDialUtil";

interface CreditRequestDetailProps {
	classes?: any;
	graphProfile?: any;
	location?: any;
	history?: any;
}

interface CreditRequestDetailState {
	creditRequest: any;
	lineItemsByReason: any[];
	expandHeader: boolean;
	reasons: any[];
	loading: { show: boolean; message: string };
	twoButtonModalState: any;
	oneButtonModalState: any;
	purchaseOrderStatus: string | null;
	isSpeedDialOpen: boolean;
	isCancelModalOpen: boolean;
	cancelLineItems: CreditRequestLineItem[];
}

const lineItemsObject = [
	{
		id: "",
		sku: "",
		modelNumber: "",
		description: "",
		quantity: 0,
		lineCostInc: 0,
		unitCostEx: 0,
		reason: "",
		status: "",
		receivedConsignmentNumber: "",
		receivedCarrierName: "",
		returnConsignmentNumber: "",
		returnCarrierName: "",
		isAutoApprove: false,
	},
];

const lineItemsByReasonObject = [["", lineItemsObject]];

const creditRequestObject = {
	id: "",
	status: "",
	purchaseOrderNumber: "",
	invoiceNumber: "",
	locationName: "",
	locationId: 0,
	invoiceType: "",
	friendlyId: "",
	supplierName: "",
	dateCreated: "",
	lineItems: lineItemsObject,
	totalCostInc: 0.0,
};

const twoButtonModalInitialState = {
	isOpen: false,
	message: "",
	primaryButtonName: "",
	secondaryButtonName: "",
	title: "",
	alertType: "",
};
const twoButtonModalStates = {
	requestReview: {
		isOpen: true,
		message: "Are you sure you want to request a review for this credit request?",
		primaryButtonName: "Yes",
		secondaryButtonName: "No",
		title: "Submit claim",
		alertType: "info",
	},
	beginReview: {
		isOpen: true,
		message: "Are you sure you want to begin reviewing this credit request?",
		primaryButtonName: "Yes",
		secondaryButtonName: "No",
		title: "Begin review",
		alertType: "info",
	},
	submitClaim: {
		isOpen: true,
		message:
			"Submitting this credit request for claim will also finalise the pending receive. Are you sure you want to go ahead?",
		primaryButtonName: "Yes",
		secondaryButtonName: "No",
		title: "Submit claim",
		alertType: "info",
	},
};
const oneButtonModalInitialState = {
	isOpen: false,
	message: "",
	primaryButtonName: "",
	title: "",
	alertType: "",
};
const oneButtonModalStates = {
	missingConsignmentNumber: {
		isOpen: true,
		message: "Consignment number must be entered before claim can be submitted.",
		primaryButtonName: "Ok",
		title: "Error",
		alertType: "error",
	},
	missingRANumber: {
		isOpen: true,
		message: "RA number must be entered before claim can be submitted.",
		primaryButtonName: "Ok",
		title: "Error",
		alertType: "error",
	},
	failedSubmission: {
		isOpen: true,
		message: "Failed to submit credit request, please try again.",
		primaryButtonName: "Ok",
		title: "Error",
		alertType: "error",
	},
	submissionCompletion: {
		isOpen: true,
		message:
			"The receive has successfully finalised and the associated credit is now claimed. A digital copy of the credit claim will be shortly emailed to relevant store team members for forwarding to supplier and archiving purposes.",
		primaryButtonName: "Ok, got it!",
		title: "Success",
		alertType: "success",
	},
	submissionPending: {
		isOpen: true,
		message:
			"The receive has successfully finalised and the associated credit is currently processing. A digital copy of the credit claim will be shortly emailed to relevant store team members for forwarding to supplier and archiving purposes.",
		primaryButtonName: "Ok, got it!",
		title: "Processing",
		alertType: "info",
	},
	missingCreditRequestId: {
		isOpen: true,
		message: "Unable to load credit request. The credit request ID is missing from the URL.",
		primaryButtonName: "Return to Home",
		title: "Missing Information",
		alertType: "error",
	},
	failedToLoadCreditRequest: {
		isOpen: true,
		message: "Failed to load credit request. Please try again or contact IT support if the issue persists.",
		primaryButtonName: "Return to Home",
		title: "Error Loading Credit Request",
		alertType: "error",
	},
	failedToCancelCreditRequest: {
		isOpen: true,
		message: "Could not cancel the credit request. Please try again or raise a support ticket if the issue persists.",
		primaryButtonName: "Ok",
		title: "Error",
		alertType: "error",
	},
};

const reasonSortOrder = [
	creditReason.shortSupply,
	creditReason.damagedAndRejected,
	creditReason.concealedDamage,
	creditReason.crackedPanel,
];
const statusSortOrder = [
	creditStatus.claimed,
	creditStatus.draft,
	creditStatus.inProgress,
	creditStatus.awaitingSupplier,
	creditStatus.pendingReview,
	creditStatus.reviewing,
	creditStatus.cancelled,
];

const cancellableCreditStatuses = [
	creditStatus.draft,
	creditStatus.inProgress,
	creditStatus.awaitingSupplier,
	creditStatus.pendingReview,
	creditStatus.reviewing,
];

const styles = () => ({
	root: {
		width: "100%",
	},
	listItem: {
		padding: "0",
	},
	marginBottom: {
		marginBottom: 10,
	},
	headerText: {
		marginTop: 25,
	},
	listContainer: {
		paddingBottom: 50
	}
});

class CreditRequestDetail extends Component<CreditRequestDetailProps, CreditRequestDetailState> {
	static contextType = PageContext;
	setStateAsync: (state: Partial<CreditRequestDetailState>) => Promise<void>;
	getPageContext: () => any;
	updatePageContext: (context: any, callback?: () => void) => void;

	constructor(props: CreditRequestDetailProps) {
		super(props);
		this.state = {
			creditRequest: creditRequestObject,
			lineItemsByReason: lineItemsByReasonObject,
			expandHeader: true,
			reasons: [],
			loading: {
				show: true,
				message: "Loading credit request details...",
			},
			twoButtonModalState: twoButtonModalInitialState,
			oneButtonModalState: oneButtonModalInitialState,
			purchaseOrderStatus: null,
			isSpeedDialOpen: false,
			isCancelModalOpen: false,
			cancelLineItems: [],
		};
		this.setStateAsync = setStateAsync.bind(this);
		this.getPageContext = getPageContext.bind(this);
		this.updatePageContext = updatePageContext.bind(this);
	}

	async componentDidMount() {
		try {
			let { creditRequestReasons } = this.getPageContext();

			// Always fetch fresh credit request from URL
			const queryStringObject = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
			const { id } = queryStringObject;

			// Validate that id exists before making API call
			if (!id) {
				await this.setStateAsync({
					oneButtonModalState: {
						...oneButtonModalStates.missingCreditRequestId,
						onPrimaryButtonClick: this.returnToHome,
					},
					loading: { ...this.state.loading, show: false },
				});
				return;
			}

			const creditRequest = await getCreditRequest(id) as any;

			await this.setStateAsync({
				creditRequest,
				loading: { ...this.state.loading, show: false },
				lineItemsByReason: this.sortAndGroupLineItems(creditRequest.lineItems),
				reasons: creditRequestReasons,
				purchaseOrderStatus: creditRequest.purchaseOrderStatus ?? null,
			});
		} catch (e) {
			console.error("Error loading credit request:", e);
			await this.setStateAsync({
				oneButtonModalState: {
					...oneButtonModalStates.failedToLoadCreditRequest,
					onPrimaryButtonClick: this.returnToHome,
				},
				loading: { ...this.state.loading, show: false },
			});
		}
	}

	sortAndGroupLineItems = (lineItems) => {
		const groupedLineItems = Object.entries(groupBy(lineItems, "reason"));
		groupedLineItems.forEach(function (gli) {
			sortArrayByStatus(gli[1], statusSortOrder);
		});
		return sortArrayByFirstValue(groupedLineItems, reasonSortOrder);
	};

	updateLineItemAsync = async (lineItem) => {
		const { creditRequest } = this.state;
		let lineItemIndex = creditRequest.lineItems.findIndex((li) => li.id === lineItem.id);
		var updatedCreditRequest = {
			...creditRequest,
			lineItems: [
				...creditRequest.lineItems.slice(0, lineItemIndex),
				{
					...(creditRequest.lineItems[lineItemIndex] = {
						...lineItem,
						modifiedByUserName: this.props.graphProfile.displayName,
						modifiedByUserId: this.props.graphProfile.staffId,
					}),
				},
				...creditRequest.lineItems.slice(lineItemIndex + 1),
			],
			modifiedByUserName: this.props.graphProfile.displayName,
			modifiedByUserId: this.props.graphProfile.staffId,
		};
		await this.setStateAsync({
			creditRequest: updatedCreditRequest,
			lineItemsByReason: this.sortAndGroupLineItems(updatedCreditRequest.lineItems),
		});
	};

	saveChangesAsync = async () => {
		await this.setStateAsync({
			loading: { show: true, message: "Saving changes..." },
		});
		let response = undefined;
		try {
			response = await postCreditRequest(this.state.creditRequest);
		} catch (e) {
			console.log("Error saving changes: ");
			console.log(e);
		}
		await this.setStateAsync({
			creditRequest: response.data,
			loading: { ...this.state.loading, show: false },
			lineItemsByReason: this.sortAndGroupLineItems(response.data.lineItems),
		});
	};

	cancelChangesAsync = async () => {
		await this.setStateAsync({ loading: { show: true, message: "Reverting changes..." } });
		let originalCreditRequest = undefined;
		try {
			originalCreditRequest = await getCreditRequest(this.state.creditRequest.id);
		} catch (e) {
			console.log("Error reverting changes: ");
			console.log(e);
		}
		await this.setStateAsync({
			creditRequest: originalCreditRequest,
			loading: { ...this.state.loading, show: false },
			lineItemsByReason: this.sortAndGroupLineItems(originalCreditRequest.lineItems),
		});
	};

	onExpandHeaderClickAsync = async () => {
		await this.setStateAsync({ expandHeader: !this.state.expandHeader });
	};

	handleSpeedDialClose = async () => {
		await this.setStateAsync({ isSpeedDialOpen: false });
	};

	handleSpeedDialOpen = async () => {
		await this.setStateAsync({ isSpeedDialOpen: true });
	};

	isCancelEligible = () => {
		const { creditRequest, purchaseOrderStatus } = this.state;
		return (
			purchaseOrderStatus?.trim().toLowerCase() === purchaseOrderStatusMapping.completed.toLowerCase() &&
			cancellableCreditStatuses.includes(creditRequest.status)
		);
	};

	cancelCreditRequestAsync = async () => {
		await this.setStateAsync({
			isSpeedDialOpen: false,
			isCancelModalOpen: true,
			cancelLineItems: this.state.creditRequest.lineItems.map((li) => ({ ...li, cancelReason: "" })),
		});
	};

	canViewReceive = () => {
		const { creditRequest } = this.state;
		return !!creditRequest.receiveId && !!creditRequest.purchaseOrderNumber && !!creditRequest.invoiceNumber
	}

	onViewReceive = () => {
		const { creditRequest } = this.state;
		this.props.history.push(`/receiving?invoiceNumber=${creditRequest.invoiceNumber}&purchaseOrderNumber=${creditRequest.purchaseOrderNumber}&id=${creditRequest.receiveId}`)
	}

	onCloseCancelModalAsync = async () => {
		await this.setStateAsync({ isCancelModalOpen: false, cancelLineItems: [] });
	};

	updateCancelLineItemAsync = async (lineItem: CreditRequestLineItem) => {
		const { cancelLineItems } = this.state;
		await this.setStateAsync({
			cancelLineItems: cancelLineItems.map((li) => (li.id === lineItem.id ? lineItem : li)),
		});
	};

	hasMissingCancelReason = (cancelLineItems: CreditRequestLineItem[]) => {
		return cancelLineItems.some((li) => !li.cancelReason);
	};

	isCancelConfirmDisabled = () => {
		return this.hasMissingCancelReason(this.state.cancelLineItems);
	};

	confirmCancelCreditRequestAsync = async () => {
		const { creditRequest, cancelLineItems } = this.state;
		if (this.hasMissingCancelReason(cancelLineItems)) {
			return;
		}
		const { displayName, staffId } = this.props.graphProfile;

		await this.setStateAsync({
			isCancelModalOpen: false,
			loading: { show: true, message: "Cancelling credit request..." },
		});

		const cancelReasonById = new Map(cancelLineItems.map((cli) => [cli.id, cli.cancelReason]));

		const cancelledCreditRequest = {
			...creditRequest,
			status: creditStatus.cancelled,
			modifiedByUserId: staffId,
			modifiedByUserName: displayName,
			dateModified: new Date(),
			lineItems: creditRequest.lineItems.map((li) => ({
				...li,
				status: creditStatus.cancelled,
				cancelReason: cancelReasonById.get(li.id) ?? "",
				modifiedByUserId: staffId,
				modifiedByUserName: displayName,
			})),
		};

		try {
			await postCreditRequest(cancelledCreditRequest);
			const refreshedCreditRequest = (await getCreditRequest(creditRequest.id)) as any;
			await this.setStateAsync({
				creditRequest: refreshedCreditRequest,
				lineItemsByReason: this.sortAndGroupLineItems(refreshedCreditRequest.lineItems),
				purchaseOrderStatus: refreshedCreditRequest.purchaseOrderStatus ?? null,
				cancelLineItems: [],
				loading: { ...this.state.loading, show: false },
			});
		} catch (e) {
			console.error("Error cancelling credit request:", e);
			await this.setStateAsync({
				oneButtonModalState: oneButtonModalStates.failedToCancelCreditRequest,
				cancelLineItems: [],
				loading: { ...this.state.loading, show: false },
			});
		}
	};

	beginReviewAsync = async (claimReason) => {
		await this.setStateAsync({
			twoButtonModalState: {
				...twoButtonModalStates.beginReview,
				onPrimaryButtonClick: () => this.postBeginReviewAsync(claimReason),
			},
		});
	};
	postBeginReviewAsync = async (claimReason) => {
		let { displayName, staffId } = this.props.graphProfile;
		await this.onCloseTwoButtonModalAsync();
		await this.setStateAsync({
			loading: { show: true, message: "Setting credit request to reviewing mode..." },
			creditRequest: {
				...this.state.creditRequest,
				lineItems: this.state.creditRequest.lineItems.map((li) => {
					if (li.reason === claimReason && li.status !== creditStatus.cancelled)
						return {
							...li,
							status: creditStatus.reviewing,
							modifiedByUserName: displayName,
							modifiedByUserId: staffId,
						};
					return li;
				}),
			},
		});
		await this.saveChangesAsync();
	};

	submitClaimAsync = async (claimReason) => {
		const { creditRequest } = this.state;
		if (creditRequest.lineItems.some((li) => li.reason === claimReason && !li.receivedConsignmentNumber)) {
			await this.setStateAsync({
				oneButtonModalState: oneButtonModalStates.missingConsignmentNumber,
			});
			return;
		} else if (creditRequest.lineItems.some((li) => li.reason === claimReason && !li.reference)) {
			await this.setStateAsync({
				oneButtonModalState: oneButtonModalStates.missingRANumber,
			});
			return;
		}
		await this.setStateAsync({
			twoButtonModalState: {
				...twoButtonModalStates.submitClaim,
				onPrimaryButtonClick: () => this.postSubmittedClaimAsync(claimReason),
			},
		});
	};
	postSubmittedClaimAsync = async (claimReason) => {
		let { displayName, staffId } = this.props.graphProfile;
		await this.onCloseTwoButtonModalAsync();
		const { creditRequest } = this.state;
		await this.setStateAsync({ loading: { show: true, message: "Submitting credit request..." } });
		// Some Ad-hoc credit requests might not be linked to a receive
		if (creditRequest.receiveId) {
			try {
				const receiveResponse = await getReceiveById(creditRequest.receiveId);
				let receive = receiveResponse.data;
				if (receive.status !== "Incomplete" && receive.status !== "Completed") {
					receive.status = "Incomplete";
					await postReceiveFinalise(receive);
				}
			} catch (e) {
				console.log("Error in credit request when updating receive: ");
				console.log(e);
				await this.onCloseTwoButtonModalAsync();
				await this.setStateAsync({
					oneButtonModalState: oneButtonModalStates.failedSubmission,
					loading: { ...this.state.loading, show: false },
				});
				return;
			}
		}
		await this.setStateAsync({
			creditRequest: {
				...creditRequest,
				lineItems: creditRequest.lineItems.map((li) => {
					if (li.reason === claimReason && li.status !== creditStatus.cancelled)
						return {
							...li,
							status: creditStatus.claimed,
							submittedByUserName: displayName,
							submittedByUserId: staffId,
							modifiedByUserName: displayName,
							modifiedByUserId: staffId,
							dateSubmitted: new Date(),
						};
					return li;
				}),
			},
		});
		await this.saveChangesAsync();
		// Make 3 attempts to get udpated credit request object from API on 1 second intervals
		await this.setStateAsync({ loading: { show: true, message: "Processing credit request..." } });
		let creditRetrievedSuccessfully = false;
		let timerId = setInterval(async () => {
			let creditRequestResponse = await getCreditRequest(creditRequest.id) as any;
			if (
				creditRequestResponse.lineItems.some((li) => li.reason === claimReason && li.status === creditStatus.claimed)
			) {
				await this.setStateAsync({
					oneButtonModalState: oneButtonModalStates.submissionCompletion,
					creditRequest: creditRequestResponse,
					loading: { ...this.state.loading, show: false },
				});
				creditRetrievedSuccessfully = true;
				clearInterval(timerId);
			}
		}, 1000);
		//Stop attempts to get updated credit request after 3.1 seconds (3 attempts)
		setTimeout(() => {
			clearInterval(timerId);
			if (!creditRetrievedSuccessfully) {
				this.setStateAsync({
					oneButtonModalState: oneButtonModalStates.submissionPending,
					loading: { ...this.state.loading, show: false },
				});
			}
		}, 3100);
	};

	onCloseTwoButtonModalAsync = async () => {
		await this.setStateAsync({ twoButtonModalState: { ...this.state.twoButtonModalState, isOpen: false } });
	};

	onCloseOneButtonModalAsync = async () => {
		await this.setStateAsync({ oneButtonModalState: { ...this.state.oneButtonModalState, isOpen: false } });
	};

	returnToHome = () => {
		this.props.history.push("/");
	};

	render() {
		return (
			<React.Fragment>
				<CreditRequestHeader
					{...this.state.creditRequest}
					expanded={this.state.expandHeader}
					onExpandClick={this.onExpandHeaderClickAsync}
				/>
				<Typography className={this.props.classes.headerText} variant="body2">
					Credit requests
				</Typography>
				<List className={this.props.classes.listContainer}>
					{this.state.lineItemsByReason &&
						this.state.lineItemsByReason.map((li) => (
							<React.Fragment key={li[0] + "fragment"}>
								<Divider />
								<ListItem key={li[0]} className={this.props.classes.listItem}>
									<CreditRequestList
										lineItems={li[1]}
										reason={li[0]}
										reasons={this.state.reasons}
										updateLineItem={this.updateLineItemAsync}
										saveChanges={this.saveChangesAsync}
										cancelChanges={this.cancelChangesAsync}
										status={li[1].find((li) => li.status !== creditStatus.cancelled)?.status}
										totalCostInc={this.state.creditRequest.totalCostInc}
										isAutoApprove={li[1].find((li) => true).isAutoApprove}
										onCancel={this.cancelCreditRequestAsync}
										onBeginReview={this.beginReviewAsync}
										onSubmitClaim={this.submitClaimAsync}
									/>
								</ListItem>
								<Divider className={this.props.classes.marginBottom} />
							</React.Fragment>
						))}
				</List>
				<LoadingDialog message={this.state.loading.message} isOpen={this.state.loading.show} />
				<ModalWithActionButton
					{...this.state.twoButtonModalState}
					onSecondaryButtonClick={this.onCloseTwoButtonModalAsync}
				/>
				<ModalWithActionButton
					{...this.state.oneButtonModalState}
					onPrimaryButtonClick={
						this.state.oneButtonModalState.onPrimaryButtonClick || this.onCloseOneButtonModalAsync
					}
				/>
				<CreditRequestDialog
					isOpen={this.state.isCancelModalOpen}
					cancelButtonName="Cancel"
					continueButtonName="Confirm"
					onCancelButtonClick={this.onCloseCancelModalAsync}
					onContinueButtonClick={this.confirmCancelCreditRequestAsync}
					isContinueButtonDisabled={this.isCancelConfirmDisabled()}
					creditRequestLineItems={this.state.cancelLineItems}
					creditRequestReasons={this.state.reasons}
					titleMessage="Are you sure you want to cancel this credit request?"
					headingText="Cancel Credit Request"
					updateLineItem={this.updateCancelLineItemAsync}
					showCopyToAllButton={false}
					forceCancelReason
				/>
				<SpeedDialMenu
					isOpen={this.state.isSpeedDialOpen}
					onOpen={this.handleSpeedDialOpen}
					onClose={this.handleSpeedDialClose}
					actions={[
						{
							title: speedDialAction.cancelCreditRequest,
							onClick: this.cancelCreditRequestAsync,
							isDisplay: this.isCancelEligible(),
						},
						{
							title: speedDialAction.viewReceive,
							onClick: this.onViewReceive,
							isDisplay: this.canViewReceive()
						}
					]}
				/>
			</React.Fragment>
		);
	}
}
export default withStyles(styles)(CreditRequestDetail);
